import * as React from 'react';
import styles from './style.module.scss';
import Editor, { Monaco } from "@monaco-editor/react";
import * as monaco from 'monaco-editor/esm/vs/editor/editor.api';
import SvgEditService from '@/services/svg-edit-service';
import DesignerService from '@/services/designer-service';
import { ToolDialog, ToolDialogOpenDirection } from '@/components/tool-dialog';
import { SvgAnnotations } from '@/components/svg-annotations';

interface State {
    scriptText: string;
}

const monacoOptions: monaco.editor.IStandaloneEditorConstructionOptions = {
    minimap: {
        enabled: false,
    }
}

export class ScriptEditor extends React.Component<{}, State> {
    private getDefaultState = () => {
        const state: State = {
            scriptText: ""
        };

        return state;
    }

    state = this.getDefaultState();

    public svgEditService = SvgEditService.Get(this, () => this.selectedElementChanged());
    public designerService = DesignerService.Get(this);

    private dialog: React.RefObject<ToolDialog> = React.createRef();

    public componentWillUnmount() {
        this.svgEditService.UnregisterComponent(this);
        this.designerService.UnregisterComponent(this);
    }

    public openToolDialog = (relativeTo: HTMLElement) => {
        this.dialog.current.open(relativeTo, ToolDialogOpenDirection.Right);
    }

    private selectedElementChanged = () => {
        if (this.svgEditService.selectedElements.length !== 1) {
            this.setState({});
            return;
        }

        const el = this.svgEditService.selectedElements[0];

        this.setState(prevState => ({
            ...prevState,
            scriptText: (el.getAttribute("pe-script") ?? "").replace(/&#10;/gi, "\n"),
        }));
    }


    private onChange = (value, event) => {
        if (this.svgEditService.selectedElements.length !== 1) {
            return;
        }

        this.svgEditService.canvas.changeSelectedAttribute("pe-script", value.replace(/\n/gi, "&#10;"), null);

        this.setState({
            scriptText: value
        });
    }

    private handleEditorWillMount = (monaco: Monaco) => {
        // Validation settings
        monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
            noSemanticValidation: true,
            noSyntaxValidation: false
        });

        // Compiler options
        monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
            target: monaco.languages.typescript.ScriptTarget.ES5,
            allowNonTsExtensions: true,
            allowJs: true,
            noLib: true,
        });

        monaco.languages.typescript.javascriptDefaults.addExtraLib(this.designerService.typeScriptDefinitions, `panelDesigner.d.ts`);
    }

    public render() {
        const disabled = this.svgEditService.selectedElements.length !== 1;
        const darkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
        const editorTheme = darkMode ? "vs-dark" : "light";

        return (
            <ToolDialog ref={this.dialog} id="pd-scripteditor" title="Script Editor" classes={styles.dialog}>
                <div className={styles.container}>
                    <h1>Element Script Editor</h1>

                    <div>
                        Use this tool to input custom JavaScript code that will be executed on this element.
                        Note: This tool is for advanced users.
                    </div>

                    {this.svgEditService.selectedElements.length > 1 && (
                        <div className="alert alert-warning">Too many elements are selected! You can only tag one element at a time!</div>
                    )}
                    {this.svgEditService.selectedElements.length == 0 && (
                        <div className="alert alert-warning">No elements selected.</div>
                    )}

                    {/*
                    <details className={styles.hintsContainer}>
                        <summary>Help</summary>
                        <p>The following global variables are available for use:</p>
                        <ul>
                            <li>element &mdash; The current element that's being acted on.</li>
                            <li>data &mdash; The current job's data, including resolved values from Automation Configuration.</li>
                            <li>Artwork &mdash; Provides functions that alter the artwork in some way.</li>
                            <li>Colours &mdash; Provides functions that return colours strings, needed to set element.fill and element.stroke.</li>
                        </ul>
                    </details>
                    */}

                    <div className={`${styles.editorContainer} ${disabled ? styles.editorContainer__disabled : ''}`}>
                        <Editor defaultLanguage="javascript" options={monacoOptions} beforeMount={this.handleEditorWillMount} theme={editorTheme}
                            defaultValue={this.state.scriptText} value={this.state.scriptText} onChange={this.onChange} />
                    </div>
                </div>
            </ToolDialog>
        );
    }
}

SvgAnnotations.RegisterAnnotationCallback("SCRIPTEDITOR", ({ element, textAnnotations }) => {
    const elScript = element.getAttribute("pe-script");

    if (elScript && elScript.trim().length > 0) {
        textAnnotations.push(`This element has a custom script attached`);
    }
});
